import React, { useEffect, useState } from "react";
import { Receipt, ChevronDown, ChevronRight, ArrowDown, ArrowUp } from "lucide-react";
import { billingAPI } from "../../services/api/billing";

const COMPONENT_LABELS = {
  observation_flat:   "Call observation",
  scenario_flat:      "Scenario run",
  llm_metric:         "LLM metric",
  non_llm_metric:     "Standard metric",
  prompt_optimization: "Optimization",
  manual_credit:      "Manual adjustment",
  plan_credit:        "Plan grant",
  plan_credit_expiry: "Plan credits expired",
  trial_credit:       "Trial credits",
  topup_purchase:     "Top-up purchase",
  refund_reversal:    "Refund reversal",
};

const BUCKET_LABELS = { plan: "plan", topup: "top-up" };

function formatDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
}

function typeClass(type) {
  return type === "credit"
    ? { wrap: "text-emerald-700", icon: ArrowUp,   sign: "+" }
    : { wrap: "text-foreground",    icon: ArrowDown, sign: "−" };
}

export default function TransactionsLedger({ defaultExpanded = false, clientId }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [txns, setTxns] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(""); // "", "credit", "debit"

  const load = async (p = page, type = filter) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 25 };
      if (type) params.type = type;
      if (clientId) params.client_id = clientId;
      const resp = await billingAPI.getTransactions(params);
      setTxns(resp.data || []);
      setTotalCount(resp.pagination?.total_count || 0);
      setTotalPages(resp.pagination?.total_pages || 1);
    } catch (e) {
      setTxns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) load(page, filter);
    // eslint-disable-next-line
  }, [expanded, page, filter, clientId]);

  // Preload summary count even when collapsed; reload on workspace switch.
  useEffect(() => {
    const params = { page: 1, per_page: 1 };
    if (clientId) params.client_id = clientId;
    billingAPI.getTransactions(params)
      .then(r => setTotalCount(r.pagination?.total_count || 0))
      .catch(() => {});
  }, [clientId]);

  return (
    <section>
      {/* Collapsed header — hidden when rendered inside a dedicated tab */}
      {!defaultExpanded && (
        <button
          onClick={() => setExpanded(v => !v)}
          className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl bg-card border border-border hover:border-border transition-colors text-left"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
              <Receipt className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div>
              <div className="text-sm font-medium text-foreground">Transactions</div>
              <div className="text-xs text-muted-foreground">
                {totalCount === 0
                  ? "No transactions yet"
                  : `${totalCount.toLocaleString()} total — view itemized credits & debits`}
              </div>
            </div>
          </div>
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </button>
      )}

      {/* Expanded body */}
      {expanded && (
        <div className={`${defaultExpanded ? "" : "mt-2"} bg-card rounded-xl border border-border`}>
          {/* Filter bar */}
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
            {[
              { v: "",       label: "All"    },
              { v: "credit", label: "Credits" },
              { v: "debit",  label: "Debits"  }
            ].map(t => {
              const active = filter === t.v;
              return (
                <button
                  key={t.v || "all"}
                  onClick={() => { setPage(1); setFilter(t.v); }}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    active ? "bg-gray-900 text-white" : "bg-muted/50 text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
            {loading && (
              <span className="text-xs text-muted-foreground ml-auto">Loading…</span>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50/50">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-5 py-2.5">Date</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 py-2.5">Type</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 py-2.5">Description</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 py-2.5">Bucket</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 py-2.5">Amount</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-5 py-2.5">Balance after</th>
                </tr>
              </thead>
              <tbody>
                {txns.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="text-center text-sm text-muted-foreground py-10">
                      No transactions in this view.
                    </td>
                  </tr>
                )}
                {txns.map(t => {
                  const { wrap, icon: Icon, sign } = typeClass(t.transaction_type);
                  return (
                    <tr key={t.id} className="border-t border-gray-50 hover:bg-muted/50/30 transition-colors">
                      <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">{formatDate(t.timestamp)}</td>
                      <td className="px-2 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${wrap}`}>
                          <Icon className="h-3 w-3" />
                          {COMPONENT_LABELS[t.component] || t.component}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-foreground">
                        <div className="line-clamp-1">{t.description || "—"}</div>
                        {t.client && (
                          <div className="text-xs text-muted-foreground mt-0.5">{t.client.name}</div>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase font-medium bg-muted text-muted-foreground tracking-wide">
                          {BUCKET_LABELS[t.balance_bucket] || t.balance_bucket || "—"}
                        </span>
                      </td>
                      <td className={`px-2 py-3 text-right tabular-nums font-medium ${wrap}`}>
                        {sign}{Number(t.amount).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">
                        {Number(t.balance_after).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border">
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages} · {totalCount.toLocaleString()} transactions
              </span>
              <div className="flex gap-1">
                <button
                  disabled={page <= 1 || loading}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="px-3 py-1 text-xs font-medium rounded-md border border-border text-muted-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  className="px-3 py-1 text-xs font-medium rounded-md border border-border text-muted-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
